import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  Card,
  Typography,
  Space,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Divider,
  Modal,
  Spin,
  Select,
} from "antd";
import {
  ArrowLeftOutlined,
  EyeOutlined,
  CodeOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { FaPen, FaTrashAlt, FaSave } from "react-icons/fa";
import { RiAddBoxFill } from "react-icons/ri";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  fetchEmailTemplates,
  addEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  getEmailMetadata,
} from "../../utils/API";
// CHANGE 1: Replaced getDynamicTags + getTableColumns with localStorage utility
import {
  getRegisteredModels,
  getModelColumns,
  registerModelColumns,
} from "../../utils/emailTemplateColumns";

import { CUSTOMER_TABLE_COLUMNS } from "../Customers/Index";
import { PROJECT_TABLE_COLUMNS } from "../projects/Index";
import { AMC_TABLE_COLUMNS } from "../AMC-Maintenance/index";
import { ATTENDANCE_TABLE_COLUMNS } from "../Attendance/index";
import { CAMPOFF_TABLE_COLUMNS } from "../Campassoff/Index";
import { CONVERSATION_TABLE_COLUMNS } from "../Companies/Index";
import { CONTACTS_TABLE_COLUMNS } from "../Contacts/Index";
import { DESIGNATION_TABLE_COLUMNS } from "../Designations/Index";
import { TEAMS_TABLE_COLUMNS } from "../Developers/Index";
import { EXPENSES_TABLE_COLUMNS } from "../Expense/Expense";
import { INVOICES_TABLE_COLUMNS } from "../Invoice/Index";
import { LEAVES_TABLE_COLUMNS } from "../Leave/index";
import { OVERTIME_TABLE_COLUMNS } from "../Overtime/index";
import { RECEIPTS_TABLE_COLUMNS } from "../Receipts/Receipts";
import { RESIGNATION_TABLE_COLUMNS } from "../Resignation/index";
import { SHIFTS_TABLE_COLUMNS } from "../Shift/index";
import { SOURCE_TABLE_COLUMNS } from "../Source/Index";
import { TAGS_TABLE_COLUMNS } from "../Tags/Index";
import { TASK_TABLE_COLUMNS } from "../Tasks/Index";
import { CALENDAR_TABLE_COLUMNS } from "../Calendar/Index";
import { ROLES_TABLE_COLUMNS } from "../Roles/Index";
import { REPORT_TABLE_COLUMNS } from "../Report/Index";
import { HOLIDAYS_TABLE_COLUMNS } from "../Holidays/Index";
import { REQUEST_ATTENDANCE_TABLE_COLUMNS } from "../MissPunch/index";
import { JOB_APPLICATIONS_TABLE_COLUMNS } from "../JobApplication/Index";

const { Text, Paragraph } = Typography;
const { Option } = Select;

const EmailTemplates = () => {
  const [view, setView] = useState<"list" | "editor">("list");
  const [templates, setTemplates] = useState([]);
  const [metadata, setMetadata] = useState<any>(null);
  const [dynamicModels, setDynamicModels] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [form, setForm] = useState({
    templateName: "",
    slug: "",
    subject: "",
    body: "",
    isActive: true,
  });

  useEffect(() => {
    const raw = localStorage.getItem("email_template_visible_columns");
    if (raw) {
      const parsed = JSON.parse(raw);
      delete parsed["Invoice"];
      delete parsed["AMC"];
      localStorage.setItem(
        "email_template_visible_columns",
        JSON.stringify(parsed),
      );
    }
    // Helper function to register models
    const registerModel = (name: string, columns: string[]) => {
      // Map string keys to objects with dataIndex and key
      const mappedCols = columns.map((key) => ({
        dataIndex: key,
        key: key, // Explicitly setting key to match utility expectation
      }));
      registerModelColumns(name, mappedCols);
    };

    // Register all imported models
    registerModel("Customers", CUSTOMER_TABLE_COLUMNS);
    registerModel("Projects", PROJECT_TABLE_COLUMNS);
    registerModel("AMC Maintenance", AMC_TABLE_COLUMNS);
    registerModel("Attendance", ATTENDANCE_TABLE_COLUMNS);
    registerModel("CampOff", CAMPOFF_TABLE_COLUMNS);
    registerModel("Conversations", CONVERSATION_TABLE_COLUMNS);
    registerModel("Contacts", CONTACTS_TABLE_COLUMNS);
    registerModel("Designations", DESIGNATION_TABLE_COLUMNS);
    registerModel("Teams", TEAMS_TABLE_COLUMNS);
    registerModel("Expenses", EXPENSES_TABLE_COLUMNS);
    registerModel("Invoices", INVOICES_TABLE_COLUMNS);
    registerModel("Leaves", LEAVES_TABLE_COLUMNS);
    registerModel("Overtime", OVERTIME_TABLE_COLUMNS);
    registerModel("Receipts", RECEIPTS_TABLE_COLUMNS);
    registerModel("Resignations", RESIGNATION_TABLE_COLUMNS);
    registerModel("Shifts", SHIFTS_TABLE_COLUMNS);
    registerModel("Source", SOURCE_TABLE_COLUMNS);
    registerModel("Tags", TAGS_TABLE_COLUMNS);
    registerModel("Tasks", TASK_TABLE_COLUMNS);
    registerModel("Calendar", CALENDAR_TABLE_COLUMNS);
    registerModel("Roles", ROLES_TABLE_COLUMNS);
    registerModel("Reports", REPORT_TABLE_COLUMNS);
    registerModel("Holidays", HOLIDAYS_TABLE_COLUMNS);
    registerModel("RequestAttendance", REQUEST_ATTENDANCE_TABLE_COLUMNS);
    registerModel("Job Application", JOB_APPLICATIONS_TABLE_COLUMNS);

    loadData();
    loadMetadata();
  }, []);

  // CHANGE 2: Load tags from localStorage when switching to editor
  useEffect(() => {
    if (view === "editor") {
      loadDynamicTagsFromStorage();
    }
  }, [view]);

  const loadData = async (search = "") => {
    setLoading(true);
    try {
      const res = await fetchEmailTemplates(search);
      setTemplates(res.data || []);
    } catch (err) {
      message.error("Error loading templates");
    }
    setLoading(false);
  };

  const loadMetadata = async () => {
    try {
      const res = await getEmailMetadata();
      setMetadata(res.data);
    } catch (err) {
      console.error("Metadata error:", err);
    }
  };

  // CHANGE 3: Replaced API-based loadDynamicTags with localStorage version
  const loadDynamicTagsFromStorage = () => {
    const models = getRegisteredModels();
    const modelTags: Record<string, string[]> = {};

    models.forEach((modelName) => {
      const columns = getModelColumns(modelName);
      if (columns.length > 0) {
        modelTags[modelName] = columns.map((col) => `{{${col.key}}}`);
      }
    });

    setDynamicModels(modelTags);
  };

  const handleStatusToggle = async (record: any) => {
    try {
      const newStatus = !record.isActive;
      await updateEmailTemplate(record._id, { ...record, isActive: newStatus });
      message.success(
        `Template ${newStatus ? "Activated" : "Deactivated"} successfully!`,
      );
      loadData();
    } catch (err: any) {
      message.error(err.response?.data?.message || "Failed to update status.");
    }
  };

  const handleSave = async () => {
    if (!form.templateName || !form.slug || !form.subject || !form.body)
      return message.error("Fill all required fields");
    setLoading(true);
    try {
      console.log("Sending form data:", form);
      console.log("currentId:", currentId);
      if (currentId) {
        await updateEmailTemplate(currentId, form);
        message.success("Template Updated Successfully");
      } else {
        await addEmailTemplate(form);
        message.success("Template Created Successfully");
      }
      setView("list");
      loadData();
    } catch (err: any) {
      console.error("Error saving template:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error status:", err.response?.status);
      console.error("Full error:", JSON.stringify(err, null, 2));
      message.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Error saving",
      );
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (deleteId) {
      try {
        await deleteEmailTemplate(deleteId);
        message.success("Template Deleted Successfully");
        loadData();
      } catch (error) {
        message.error("Error deleting template");
      } finally {
        setDeleteId(null);
      }
    }
  };

  const renderPreview = (html: string) => {
    if (!metadata || !metadata.previewValues) return html;
    let preview = html;
    Object.keys(metadata.previewValues).forEach((key) => {
      const regex = new RegExp(`{{${key}}}`, "g");
      preview = preview.replace(regex, metadata.previewValues[key]);
    });
    return preview;
  };

  const columns = [
    {
      title: "#",
      render: (_: any, __: any, index: number) => index + 1,
      width: 60,
    },
    {
      title: "Template Name",
      dataIndex: "templateName",
      width: 200,
      render: (t: string) => (
        <Text
          strong
          style={{ color: "#1a3353", width: 200, display: "block" }}
          ellipsis={{ tooltip: t }}
        >
          {t || "-"}
        </Text>
      ),
    },
    {
      title: "Slug",
      dataIndex: "slug",
      render: (s: string) => <Tag color="blue">{s || "-"}</Tag>,
    },
    {
      title: "Subject Line",
      dataIndex: "subject",
      width: 300,
      render: (text: string) => (
        <Text
          style={{ width: 300, display: "block" }}
          ellipsis={{ tooltip: text }}
        >
          {text || "-"}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      align: "center" as const,
      render: (isActive: boolean, record: any) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <label
            style={{
              position: "relative",
              display: "inline-block",
              width: 40,
              height: 22,
              backgroundColor: isActive ? "#001529" : "#f0f0f0",
              borderRadius: 13,
              cursor: "pointer",
              transition: "background-color 0.3s ease",
              boxShadow: isActive
                ? "inset 0 0 0 2px #001529"
                : "inset 0 0 0 1px #ccc",
            }}
          >
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => handleStatusToggle(record)}
              style={{ display: "none" }}
            />
            <span
              style={{
                position: "absolute",
                width: 21,
                height: 19,
                left: isActive ? "calc(99% - 21px)" : 2,
                top: 1,
                backgroundColor: "#ffffff",
                borderRadius: "50%",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                transition: "left 0.3s ease, box-shadow 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: "bold",
                color: isActive ? "#001529" : "#666",
              }}
            >
              {isActive ? "ON" : "OFF"}
            </span>
          </label>
        </div>
      ),
    },
    {
      title: "Action",
      align: "center" as const,
      render: (_: any, record: any) => (
        <div
          className="d-flex"
          style={{ gap: "8px", alignItems: "center", justifyContent: "center" }}
        >
          <Tooltip title="Edit">
            <button
              onClick={() => {
                setCurrentId(record._id);
                setForm(record);
                setView("editor");
              }}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 shadow-[0_4px_12px_rgba(139,92,246,0.3)] hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] active:translate-y-[1px] active:shadow-none text-white flex items-center justify-center transition-all duration-200 ease-in-out group"
            >
              <FaPen
                className="group-hover:scale-110 transition-transform duration-200"
                size={12}
              />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button
              onClick={() => setDeleteId(record._id)}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 shadow-[0_4px_12px_rgba(244,63,94,0.3)] hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] active:translate-y-1 active:shadow-none text-white flex items-center justify-center transition-all duration-200 group"
            >
              <FaTrashAlt
                className="group-hover:scale-110 transition-transform"
                size={12}
              />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const labelStyle = {
    color: "#4b5563",
    fontFamily: "inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    display: "block",
    marginBottom: "8px",
    paddingLeft: "4px",
  };

  return (
    <div
      style={{ padding: "8px 5px", background: "#f0f2f5", minHeight: "auto" }}
    >
      {view === "list" ? (
        <>
          <Row
            align="middle"
            justify="space-between"
            gutter={[16, 16]}
            style={{ marginBottom: "10px", padding: "0 5px" }}
          >
            <Col>
              <h2 className="headername">Email Templates</h2>
            </Col>
            <Col xs={24} md={12} lg={10}>
              <div className="flex flex-col sm:flex-row gap-2 w-full justify-end">
                <Input
                  prefix={<SearchOutlined />}
                  size="large"
                  placeholder="Search..."
                  allowClear
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    loadData(e.target.value);
                  }}
                  style={{ width: 220, height: "33px" }}
                />
                <Button
                  type="primary"
                  onClick={() => {
                    setCurrentId(null);
                    setForm({
                      templateName: "",
                      slug: "",
                      subject: "",
                      body: "",
                      isActive: true,
                    });
                    setView("editor");
                  }}
                  className="allbuttons"
                >
                  <RiAddBoxFill className="button-icon" />
                  <span className="button-text">Add</span>
                </Button>
              </div>
            </Col>
          </Row>

          <Card
            bordered={false}
            style={{
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
            bodyStyle={{ padding: "8px" }}
          >
            <Spin spinning={loading}>
              <div
                className="scrollbar-hide custom-scroll-table"
                style={{ overflowX: "auto" }}
              >
                <Table
                  dataSource={templates}
                  columns={columns}
                  rowKey="_id"
                  scroll={{ x: "max-content" }}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                />
              </div>
            </Spin>
          </Card>
        </>
      ) : (
        <Card
          bordered={false}
          style={{
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
          bodyStyle={{ padding: "20px" }}
          title={
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={() => setView("list")}
              style={{ padding: 0, color: "#1a3353" }}
              className="back-btn"
            >
              Back to List
            </Button>
          }
        >
          <Row gutter={24}>
            <Col span={16}>
              <Space direction="vertical" style={{ width: "100%" }} size={32}>
                <div>
                  <Text style={labelStyle}>Template Name</Text>
                  <Input
                    placeholder="Internal identification name"
                    size="large"
                    className="Allinput"
                    value={form.templateName}
                    onChange={(e) => {
                      const templateName = e.target.value;
                      const autoSlug = templateName
                        .toLowerCase()
                        .replace(/[^a-z0-9\s-]/g, "")
                        .trim()
                        .replace(/\s+/g, "-")
                        .replace(/-+/g, "-");
                      setForm({ ...form, templateName, slug: autoSlug });
                    }}
                  />
                </div>
                <div>
                  <Text style={labelStyle}>Slug</Text>
                  <Input
                    placeholder="unique-identifier-for-template"
                    size="large"
                    className="Allinput"
                    value={form.slug}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        slug: e.target.value.toLowerCase().replace(/\s+/g, "-"),
                      })
                    }
                  />
                </div>
                <div>
                  <Text style={labelStyle}>Subject Line</Text>
                  <Input
                    placeholder="Email subject for customers"
                    size="large"
                    className="Allinput"
                    value={form.subject}
                    onChange={(e) =>
                      setForm({ ...form, subject: e.target.value })
                    }
                  />
                </div>

                <div
                  style={{
                    border: "1px solid #d9d9d9",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: "10px 15px",
                      background: "#fafafa",
                      borderBottom: "1px solid #d9d9d9",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Text strong>Email Body Content</Text>
                    <Button
                      size="small"
                      type={previewMode ? "primary" : "default"}
                      icon={previewMode ? <CodeOutlined /> : <EyeOutlined />}
                      onClick={() => setPreviewMode(!previewMode)}
                    >
                      {previewMode ? "Editor" : "Preview"}
                    </Button>
                  </div>
                  {previewMode ? (
                    <div
                      className="ql-container ql-snow"
                      style={{ border: "none" }}
                    >
                      <div
                        className="ql-editor"
                        style={{
                          padding: "20px",
                          minHeight: "400px",
                          background: "#fff",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: renderPreview(form.body),
                        }}
                      />
                    </div>
                  ) : (
                    <ReactQuill
                      theme="snow"
                      value={form.body}
                      onChange={(v) => setForm({ ...form, body: v })}
                      style={{ height: "400px", marginBottom: "45px" }}
                    />
                  )}
                </div>
              </Space>
            </Col>
            <Col span={8}>
              <Card
                title={
                  <Space>
                    <InfoCircleOutlined /> Dynamic Tags
                  </Space>
                }
                size="small"
                style={{ background: "#f9f9f9" }}
              >
                <div style={{ marginBottom: "15px" }}>
                  <Text
                    strong
                    style={{ display: "block", marginBottom: "8px" }}
                  >
                    Select Model
                  </Text>
                  <Select
                    placeholder="Choose a model"
                    style={{ width: "100%" }}
                    className="CustomSelect"
                    value={selectedModel}
                    onChange={(value) => setSelectedModel(value)}
                  >
                    {Object.keys(dynamicModels).map((modelName) => (
                      <Option key={modelName} value={modelName}>
                        {modelName}
                      </Option>
                    ))}
                  </Select>
                </div>
                <Paragraph type="secondary" style={{ fontSize: "12px" }}>
                  Click to insert a dynamic tag.
                </Paragraph>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {selectedModel &&
                    dynamicModels[selectedModel]?.map((ph: string) => (
                      <Tag
                        key={ph}
                        color="processing"
                        style={{ cursor: "pointer", padding: "4px 8px" }}
                        onClick={() =>
                          setForm((f) => ({ ...f, body: f.body + ph }))
                        }
                      >
                        {ph}
                      </Tag>
                    ))}
                  {!selectedModel && (
                    <Text type="secondary">
                      Please select a model from your sidebar
                    </Text>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
          <Divider />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              marginTop: "10px",
            }}
          >
            <button
              onClick={() => {
                setForm({
                  templateName: "",
                  slug: "",
                  subject: "",
                  body: "",
                  isActive: true,
                });
                setSelectedModel(null);
                setCurrentId(null);
                setView("list");
              }}
              className="allcanclebutton"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="allsubmitebutton"
              style={{ width: "120px", opacity: loading ? 0.8 : 1 }}
            >
              {loading ? (
                <Space>
                  <Spin size="small" />
                  <span>Saving...</span>
                </Space>
              ) : (
                <Space>
                  <FaSave />
                  <span>Save</span>
                </Space>
              )}
            </button>
          </div>
        </Card>
      )}
      <Modal
        open={!!deleteId}
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p style={{ margin: 0 }}>
          Are you sure you want to delete this Email Template?
        </p>
      </Modal>
    </div>
  );
};

export default EmailTemplates;
